import React, { useEffect, useState } from "react";
import { createCustomerAddress, updateCustomerAddress } from "../../api/post/createNewAddress";
import { companyAddressUpdate } from "../../api/post/companyAddressUpdateApi";
// import { setShowAddressForm } from "../../redux/features/customer/customerSlice";
import { updateCompanyAddress } from "../../redux/features/company/companySlice";
import { useDispatch, useSelector } from "react-redux";
import { selectAreas } from "../../redux/features/country/countrySelectors";
import SelectField from "./areasDropdown";
import { tabs } from "../../constants/restaurants";
import { formFields, saveAddressPayload } from "../../utils/addresses";
import {
  selectSearchedCustomer,
  selectSearchedCustomerId,
  selectEditingAddress,
  selectIsEditMode,
  selectCustomerAddresses,
  selectServiceType
} from "../../redux/features/customer/customerSelectors";
import {
  selectCateringSearchedCustomer,
  selectCateringEditingAddress,
  selectCateringIsEditMode,
  selectCateringCustomerAddresses
} from "../../redux/features/cateringCustomer/cateringCustomerSelectors";
import {
  selectSearchedCompany
} from "../../redux/features/company/companySelectors";
import { searchCustomerAsync } from "../../redux/features/customer/customerThunks";
import { searchCateringCustomerAsync } from "../../redux/features/cateringCustomer/cateringCustomerThunks";
import { createCompanyAddressAsync } from "../../redux/features/company/companyThunks";

const InputField = ({ placeholder, className, error, ...props }) => (
  <div className="w-full">
    <input
      placeholder={placeholder}
      className={`border border-slate-300 bg-neutral-50 focus:ring-1 focus:ring-slate-300 focus:outline-none placeholder-gray-400 placeholder-bold px-4 py-2 w-full sm:w-[427px] h-12 rounded-xl ${className} ${error ? "border-red-500 placeholder-red-600" : ""
        }`}
      {...props}
    />
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

const TextAreaField = ({ placeholder, className, ...props }) => (
  <textarea
    placeholder={placeholder}
    className={`border border-slate-300 bg-neutral-50 focus:ring-1 focus:ring-slate-300 focus:outline-none placeholder-gray-400 placeholder-bold px-4 py-2 w-full sm:w-[427px] h-[140px] rounded-xl ${className}`}
    {...props}
  />
);

const AddressForm = ({ setShowAddressForm }) => {
  const serviceType = useSelector(selectServiceType);
  const searchedCompany = useSelector(selectSearchedCompany);

  // Determine if we're in catering context (individual or corporate)
  const isCateringContext = serviceType === "individual catering" || serviceType === "corporate catering";
  const isCompanyContext = serviceType === "corporate catering" && searchedCompany;

  // Use appropriate selectors based on service type
  const regularCustomer = useSelector(selectSearchedCustomer);
  const cateringCustomer = useSelector(selectCateringSearchedCustomer);
  const customer = isCateringContext ? cateringCustomer : regularCustomer;

  const regularEditingAddress = useSelector(selectEditingAddress);
  const cateringEditingAddress = useSelector(selectCateringEditingAddress);
  const editingAddress = isCateringContext ? cateringEditingAddress : regularEditingAddress;

  const regularIsEditMode = useSelector(selectIsEditMode);
  const cateringIsEditMode = useSelector(selectCateringIsEditMode);
  const isEditMode = isCateringContext ? cateringIsEditMode : regularIsEditMode;

  const regularCustomerAddresses = useSelector(selectCustomerAddresses);
  const cateringCustomerAddresses = useSelector(selectCateringCustomerAddresses);
  const customerAddresses = isCateringContext ? cateringCustomerAddresses : regularCustomerAddresses;

  // Determine address type from editing address or default to House
  const getInitialAddressType = () => {
    if (editingAddress && editingAddress.address_type) {
      return editingAddress.address_type;
    }
    return "House";
  };

  const [activeTab, setActiveTab] = useState(getInitialAddressType());
  const [address, setAddress] = useState({
    customerId: customer?.id,
    address_type: activeTab,
    area: "",
    block: "",
    street: "",
    avenue: "",
    houseNumber: "",
    building: "",
    floor: "",
    apartmentNo: "",
    companyName: "",
    addressLabel: "",
    additionalDirections: "",
    alternativePhone: "",
  });
  const [errors, setErrors] = useState({});
  const areas = useSelector(selectAreas);

  const dispatch = useDispatch();

  // Add effect to update address_type when activeTab changes
  useEffect(() => {
    setAddress(prev => ({
      ...prev,
      address_type: activeTab
    }));
  }, [activeTab]);

  // Update form when editing address changes
  useEffect(() => {
    if (editingAddress) {
      console.log("Editing address:", editingAddress);

      let originalAddress = null;

      // Find the original address data from the appropriate source
      if (isCompanyContext && searchedCompany?.addresses) {
        // For company addresses, look in searchedCompany.addresses
        originalAddress = searchedCompany.addresses.find(addr => addr.id === editingAddress.id);
      } else if (customerAddresses) {
        // For customer addresses, look in customerAddresses
        originalAddress = customerAddresses.find(addr => addr.id === editingAddress.id);
      }

      if (originalAddress) {
        console.log("Found original address data:", originalAddress);

        // Set the active tab based on address type
        const addressType = originalAddress.address_type || "house";
        setActiveTab(addressType.charAt(0).toUpperCase() + addressType.slice(1)); // Capitalize first letter

        // Map the API fields to form fields
        setAddress({
          customerId: customer?.id,
          address_type: addressType, // Use the original address type
          area: originalAddress.delivery_charge_area_name_en || originalAddress.area || "",
          block: originalAddress.block || "",
          street: originalAddress.street || "",
          avenue: originalAddress.avenue || "",
          houseNumber: originalAddress.house_no || "",
          building: originalAddress.building || "",
          floor: originalAddress.floor || "",
          apartmentNo: originalAddress.apartment_no || "",
          companyName: originalAddress.company_name || "",
          addressLabel: originalAddress.address_label || "",
          additionalDirections: originalAddress.additional_direction || "",
          alternativePhone: originalAddress.phone_no || originalAddress.mobile_number || "",
          id: originalAddress.id || "",
        });
      }
    }
  }, [editingAddress, customerAddresses, customer, isCompanyContext, searchedCompany]);

  // Preload images on mount
  useEffect(() => {
    tabs.forEach((tab) => {
      new Image().src = tab.activeImgSrc;
      new Image().src = tab.inactiveImgSrc;
    });
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAddress((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Dynamically render the rest of the fields from formFields
  const renderFormFields = (fields) => {
    // Add validation to ensure fields is an array
    if (!Array.isArray(fields)) {
      console.warn(`No form fields found for tab: ${activeTab}`);
      return null;
    }

    return fields.map(({ placeholder, className, name }, index) => {
      if (!Object.prototype.hasOwnProperty.call(address, name)) return null;
      const isError = errors[name];
      return (
        <InputField
          key={index}
          placeholder={placeholder}
          name={name}
          className={`${className} ${isError ? "border-red-600 placeholder-red-600" : ""
            }`}
          value={address[name]}
          onChange={handleInputChange}
        />
      );
    });
  };

  // Validate required fields
  const validateForm = () => {
    // Normalize the activeTab to match formFields keys
    const normalizedTab = activeTab.charAt(0).toUpperCase() + activeTab.slice(1).toLowerCase();
    const currentFields = formFields[normalizedTab];
    const newErrors = {};

    // 1) Validate required fields from formFields
    if (Array.isArray(currentFields)) {
      currentFields.forEach(({ name, placeholder }) => {
        if (placeholder.includes("Required") && !address[name]) {
          newErrors[name] = `${placeholder} is required`;
        }
      });
    }

    // 2) Validate new dropdowns if required
    if (!address.area) {
      newErrors.area = "Area is required";
    }
    console.log(newErrors, "newErrors");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitAddress = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      if (isCompanyContext) {
        // Company address creation/update
        if (isEditMode && editingAddress) {
          console.log("Updating company address with ID:", editingAddress.id);

          // Prepare company address update payload
          const companyAddressUpdatePayload = {
            company_id: searchedCompany.id,
            address_type: address.address_type.toLowerCase(),
            block: address.block,
            street: address.street,
            avenue: address.avenue,
            house_no: address.houseNumber,
            apartment_no: address.apartmentNo,
            building: address.building,
            floor: address.floor,
            company_name: searchedCompany.name,
            address_label: address.addressLabel,
            additional_direction: address.additionalDirections,
            country_code: "+965", // Default for Kuwait
            mobile_number: address.alternativePhone,
          };

          const updatedAddressResponse = await companyAddressUpdate(editingAddress.id, companyAddressUpdatePayload);

          // Update the address in Redux state
          dispatch(updateCompanyAddress({
            companyId: searchedCompany.id,
            addressId: editingAddress.id,
            updatedAddress: updatedAddressResponse.data
          }));

          // Close the form
          if (setShowAddressForm) {
            setShowAddressForm(false);
          }
        } else {
          console.log("Creating company address for company:", searchedCompany.id);

          // Prepare company address payload
          const companyAddressPayload = {
            company_id: searchedCompany.id,
            address_type: address.address_type.toLowerCase(),
            block: address.block,
            street: address.street,
            avenue: address.avenue,
            house_no: address.houseNumber,
            apartment_no: address.apartmentNo,
            building: address.building,
            floor: address.floor,
            company_name: searchedCompany.name,
            address_label: address.addressLabel,
            additional_direction: address.additionalDirections,
            country_code: "+965", // Default for Kuwait
            mobile_number: address.alternativePhone,
            // Add other required fields as needed
          };

          dispatch(createCompanyAddressAsync(companyAddressPayload, searchedCompany.id));

          // Close the form after creating company address
          if (setShowAddressForm) {
            setShowAddressForm(false);
          }
        }
      } else {
        // Customer address creation/update
        const addressPayload = saveAddressPayload({
          ...address,
          address_type: address.address_type.toLowerCase(),
        });

        if (isEditMode && editingAddress) {
          console.log("Updating address with ID:", editingAddress.id);
          // Make sure customer_id is correct for updates
          const updatePayload = {
            ...addressPayload,
            customer_id: customer.id // Ensure correct customer_id is used
          };
          await updateCustomerAddress(updatePayload, editingAddress.id);
        } else {
          // Otherwise create a new address
          await createCustomerAddress(addressPayload);
        }

        // Refresh data and close the form - use appropriate thunk based on context
        if (isCateringContext) {
          dispatch(searchCateringCustomerAsync(customer?.mobile_country_code, customer?.mobile_no));
        } else {
          dispatch(searchCustomerAsync(customer?.mobile_country_code, customer?.mobile_no));
        }
        if (setShowAddressForm) {
          setShowAddressForm(false);
        }
      }
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} address:`, error);
    }
  };

  const renderForm = () => (
    <form onSubmit={handleSubmitAddress} className="mt-4 space-y-4">
      <SelectField
        placeholder="Select Area (Required)"
        options={areas}
        value={address.area}
        onChange={handleInputChange}
        error={errors.area}
      />

      {/* Render other fields dynamically */}
      <div className="flex flex-col gap-4">
        {renderFormFields(formFields[activeTab.charAt(0).toUpperCase() + activeTab.slice(1).toLowerCase()])}

        <TextAreaField
          placeholder="Additional Direction"
          name="additionalDirections"
          value={address.additionalDirections}
          onChange={handleInputChange}
          className={
            errors.additionalDirections
              ? "border-red-600 placeholder-red-600"
              : ""
          }
        />

        <label className="flex justify-center text-black text-xl font-bold">
          Contact Details
        </label>
        <div className="flex flex-col sm:flex-row gap-3">
          <InputField
            placeholder="+965 (KW)"
            readOnly
            className="w-full sm:w-[40%] lg:w-[133px]"
          />
          <InputField
            placeholder="Alternative Phone Number"
            name="alternativePhone"
            value={address.alternativePhone}
            onChange={handleInputChange}
            className={
              errors.alternativePhone
                ? "border-red-600 placeholder-red-600"
                : "w-full sm:w-[60%] lg:w-[284px]"
            }
          />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 sm:gap-16 justify-between">
        <button
          type="button"
          className="bg-white text-amber-800 px-4 py-2 border border-amber-800 rounded-md w-full sm:w-2/3 lg:w-[222px] h-10"
          onClick={() => {
            if (setShowAddressForm) {
              setShowAddressForm(false);
            }
          }}
        >
          Back
        </button>
        <button
          type="submit"
          className="text-white bg-amber-800 px-4 py-2 rounded-md w-full sm:w-2/3 lg:w-[222px] h-10"
        >
          {isEditMode ? "Update Address" : "Save Address"}
        </button>
      </div>
    </form>
  );

  return (
    <div className="fixed top-20 right-0 bottom-0 max-w-[29rem] sm:ml-auto p-4 bg-neutral-100 overflow-y-auto">
      <div className="flex justify-between mb-4">
        {tabs.map((tab) => (
          <button
            key={tab.label}
            onClick={() => setActiveTab(tab.label)}
            className={`flex flex-col items-center justify-center text-sm flex-1 mx-1 ${activeTab === tab.label
                ? "bg-amber-800 text-white h-[80px] rounded-xl"
                : "bg-white h-[80px] rounded-xl"
              }`}
          >
            <div className="flex items-center justify-center">
              <img
                src={
                  activeTab === tab.label
                    ? tab.activeImgSrc
                    : tab.inactiveImgSrc
                }
                alt={`${tab.label} icon`}
                className="w-[34px] h-9"
              />
              <span className="mt-3 ml-3 text-sm">{tab.label}</span>
            </div>
          </button>
        ))}
      </div>
      <div className="pb-20">
        {renderForm()}
      </div>
    </div>
  );
};

export default AddressForm;
